// Updated ROIFormPage.jsx with combined authorization section

import React, { useState, useRef, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { todayEastern } from '../dateUtils';

export default function ROIFormPage() {
  // State management
  const [token, setToken] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [allowEdits, setAllowEdits] = useState(true);
  const [isEAPClient, setIsEAPClient] = useState(false);

  // Client and pre-filled data
  const [clientData, setClientData] = useState({});
  const [preFilledData, setPreFilledData] = useState({});

  // Form fields - Updated with combined authorization options
  const [formData, setFormData] = useState({
    // Client information
    clientName: '',
    clientDob: '',
    clientAddress: '',
    clientPhone: '',
    
    // Release to information
    authorizeReleaseTo: '',
    releaseToRelationship: '',
    releaseToPhone: '',
    releaseToEmail: '',
    
    // COMBINED: Information to be shared/released - all under one section
    shareProgressWithProvider: false,
    coordinateCareWithPsychiatrist: false,
    shareWithSchool: false,
    speakWithFamily: false,
    billUsingOtherCard: false,
    provideDocumentationToProvider: false,
    allowEmergencyContactInform: false,
    schedulingInfoOnly: false,
    treatmentProgress: false,
    diagnosisAndPlan: false,
    billingAndPayment: false,
    fullClinicalRecords: false,
    releaseOtherSpecify: '',
    
    // Purpose of disclosure
    purposeCoordinationCare: false,
    purposeBilling: false,
    purposeEmergency: false,
    purposeSupportFromFamily: false,
    purposeOtherSpecify: '',
    
    // Release duration
    durationOneYear: false,
    durationEndOfTreatment: false,
    durationSpecificDate: false,
    specificExpirationDate: '',
    
    // EAP section
    eapAuthorizeSharing: false,
    eapUnderstandNoDetails: false,
    eapEmployer: '',
    eapUnderstandRefusalImpact: false,
    
    // Guardian information (for minors)
    guardianName: '',
    guardianRelationship: '',
    
    // Witness information
    witnessName: ''
  });

  // Signature states
  const [clientSignatureData, setClientSignatureData] = useState(null);
  const [guardianSignatureData, setGuardianSignatureData] = useState(null);
  const [witnessSignatureData, setWitnessSignatureData] = useState(null);

  // Date the client says they signed — defaults to today, may be backdated, never future
  const [signatureDate, setSignatureDate] = useState(todayEastern());

  // Signature refs
  const clientSigRef = useRef(null);
  const guardianSigRef = useRef(null);
  const witnessSigRef = useRef(null);
  
  // Form navigation
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 2;

  // Token validation
  useEffect(() => {
    // Skip validation in development
    if (window.location.hostname === 'localhost') {
      setIsValid(true);
      setLoading(false);
      return;
    }
    
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (!t) {
      setErrorMsg('No token provided');
      setLoading(false);
      return;
    }
    
    setToken(t);
    
    // Validate token with API
    fetch(`/api/forms/roi/validate?token=${t}`)
      .then(res => res.json())
      .then(res => {
        if (res.success) {
          setIsValid(true);
          setClientData(res.clientData || {});
          setPreFilledData(res.preFilledData || {});
          setAllowEdits(res.preFilledData?.allowClientEdits !== false);
          
          setIsEAPClient(res.preFilledData?.isEAPClient || false);
          
          // Pre-fill form with client and pre-filled data
          const formatDateForInput = (dateStr) => {
            if (!dateStr) return '';
            if (dateStr.includes('T')) {
              return dateStr.split('T')[0];
            }
            return dateStr;
          };

          setFormData(prev => ({
            ...prev,
            clientName: res.clientData.client_name || '',
            clientDob: formatDateForInput(res.clientData.dob),
            clientAddress: `${res.clientData.address || ''} ${res.clientData.city || ''} ${res.clientData.state || ''} ${res.clientData.zip_code || ''}`.trim(),
            clientPhone: res.clientData.phone || '',
            guardianName: res.clientData.guardian_name || '',
            ...res.preFilledData,
            specificExpirationDate: formatDateForInput(res.preFilledData?.specificExpirationDate || '')
          }));
        } else {
          setErrorMsg(res.error || 'Invalid link');
        }
        setLoading(false);
      })
      .catch(error => {
        console.error('Validation error:', error);
        setErrorMsg('Error validating link');
        setLoading(false);
      });
  }, []);

  // Set default expiration date to 1 year from now
  useEffect(() => {
    if (!formData.specificExpirationDate) {
      const oneYearFromNow = new Date();
      oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1);
      oneYearFromNow.setMinutes(oneYearFromNow.getMinutes() - oneYearFromNow.getTimezoneOffset());
      const formattedDate = oneYearFromNow.toISOString().split('T')[0];
      setFormData(prev => ({
        ...prev,
        specificExpirationDate: formattedDate
      }));
    }
  }, []);

  // Handle form field changes
  const handleInputChange = (field, value) => {
    if (!allowEdits) return;
    
    if (field === 'clientDob' || field === 'specificExpirationDate') {
      if (value && value.includes('T')) {
        value = value.split('T')[0];
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Print function
  const handlePrint = () => {
    if (submitted && submittedData) {
      const printWindow = window.open('', '_blank', 'width=800,height=900');
      if (!printWindow) {
        alert('Failed to open new window for printing. Please check your popup blocker settings.');
        return;
      }

      const printContent = generatePrintContent(submittedData);
      
      printWindow.document.write(printContent);
      printWindow.document.close();
      
      printWindow.onload = function() {
        printWindow.focus();
        printWindow.print();
      };
    } else {
      window.print();
    }
  };

  // Generate print content with form data
  const generatePrintContent = (data) => {
    const signedDate = formatDate(data.clientSignatureDate || todayEastern());
    
    return `
      <html>
      <head>
        <title>Authorization for Release of Information</title>
        <style>
          body {
            font-family: 'Times New Roman', Times, serif;
            margin: 20px;
            padding: 20px;
            line-height: 1.5;
            font-size: 12px;
            color: var(--text-strong);
          }
          .page {
            max-width: 800px;
            margin: 0 auto;
          }
          .header {
            text-align: center;
            margin-bottom: 30px;
          }
          .header img {
            max-width: 100%;
            height: auto;
          }
          .footer {
            text-align: center;
            margin-top: 40px;
            margin-bottom: 20px;
          }
          .footer img {
            max-width: 100%;
            height: auto;
          }
          .form-title {
            text-align: center;
            color: var(--primary-color);
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 30px;
            padding: 15px;
            border: 2px solid var(--primary-color);
            background: var(--surface-muted);
          }
          .review-document {
            border: 2px solid var(--primary-color);
            padding: 30px;
            margin: 20px 0;
            background: #fff;
          }
          .review-section {
            margin-bottom: 25px;
            padding-bottom: 15px;
            border-bottom: 1px solid var(--border-color);
          }
          .review-section:last-child {
            border-bottom: none;
          }
          .review-section h3 {
            color: var(--primary-color);
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 10px;
            border-bottom: 1px solid var(--border-color);
            padding-bottom: 5px;
          }
          .review-section p {
            margin: 8px 0;
            line-height: 1.4;
            font-size: 12px;
          }
          .review-checkboxes p {
            margin: 5px 0;
            padding-left: 10px;
          }
          .signature-section {
            margin: 20px 0;
            border: 2px solid var(--primary-color);
            padding: 15px;
            background: var(--surface-muted);
          }
          .signature-img {
            max-width: 300px;
            max-height: 80px;
            border: 1px solid var(--text-strong);
            margin: 5px 0;
            background: white;
          }
        </style>
      </head>
      <body>
        <div class="page">
          <div class="header">
            <img src="https://www.avenuesportal.com/letterhead.png" alt="Avenues Unlimited">
          </div>
          
          <div class="form-title">AUTHORIZATION FOR RELEASE OF INFORMATION</div>
          
          <div class="review-document">
            <div class="review-section">
              <h3>Client Information</h3>
              <p><strong>Name:</strong> ${data.clientName}</p>
              <p><strong>Date of Birth:</strong> ${formatDate(data.clientDob)}</p>
              <p><strong>Phone:</strong> ${data.clientPhone}</p>
              <p><strong>Address:</strong> ${data.clientAddress}</p>
            </div>
            
            <div class="review-section">
              <h3>Information Release Authorization</h3>
              <p><strong>Release information to:</strong> ${data.authorizeReleaseTo}</p>
              <p><strong>Relationship:</strong> ${data.releaseToRelationship}</p>
              <p><strong>Contact:</strong> ${data.releaseToPhone}</p>
            </div>
            
            <div class="review-section">
              <h3>Authorized Information Release</h3>
              <div class="review-checkboxes">
                ${data.shareProgressWithProvider ? '<p>✓ Share progress and treatment details with my primary care provider or psychiatrist</p>' : ''}
                ${data.coordinateCareWithPsychiatrist ? '<p>✓ Coordinate care with my psychiatrist or prescribing provider</p>' : ''}
                ${data.shareWithSchool ? '<p>✓ Share treatment information with my child\'s school</p>' : ''}
                ${data.speakWithFamily ? '<p>✓ Speak with a family member, partner, or support person</p>' : ''}
                ${data.billUsingOtherCard ? '<p>✓ Bill services using a card not in my name</p>' : ''}
                ${data.provideDocumentationToProvider ? '<p>✓ Provide documentation to another mental health provider</p>' : ''}
                ${data.allowEmergencyContactInform ? '<p>✓ Allow emergency contacts to be informed in case of a crisis</p>' : ''}
                ${data.schedulingInfoOnly ? '<p>✓ Scheduling or appointment information only</p>' : ''}
                ${data.treatmentProgress ? '<p>✓ Treatment progress and participation</p>' : ''}
                ${data.diagnosisAndPlan ? '<p>✓ Diagnosis and treatment plan</p>' : ''}
                ${data.billingAndPayment ? '<p>✓ Billing and payment details</p>' : ''}
                ${data.fullClinicalRecords ? '<p>✓ Full clinical records</p>' : ''}
                ${data.releaseOtherSpecify ? `<p>✓ Other: ${data.releaseOtherSpecify}</p>` : ''}
              </div>
            </div>
            
            <div class="review-section">
              <h3>Purpose of Disclosure</h3>
              <div class="review-checkboxes">
                ${data.purposeCoordinationCare ? '<p>✓ Coordination of care</p>' : ''}
                ${data.purposeBilling ? '<p>✓ Billing</p>' : ''}
                ${data.purposeEmergency ? '<p>✓ Emergency or safety planning</p>' : ''}
                ${data.purposeSupportFromFamily ? '<p>✓ Support from family/friends</p>' : ''}
                ${data.purposeOtherSpecify ? `<p>✓ Other: ${data.purposeOtherSpecify}</p>` : ''}
              </div>
            </div>
            
            <div class="review-section">
              <h3>Duration</h3>
              <p>
                ${data.durationOneYear ? 'One year from today' : ''}
                ${data.durationEndOfTreatment ? 'End of treatment' : ''}
                ${data.durationSpecificDate ? `Until: ${formatDate(data.specificExpirationDate)}` : ''}
              </p>
            </div>
            
            ${(data.eapAuthorizeSharing || data.eapEmployer) ? `
            <div class="review-section">
              <h3>Employee Assistance Program (EAP)</h3>
              ${data.eapEmployer ? `<p><strong>Employer:</strong> ${data.eapEmployer}</p>` : ''}
              <div class="review-checkboxes">
                ${data.eapAuthorizeSharing ? '<p>✓ Authorized to share session dates and billing information with employer</p>' : ''}
                ${data.eapUnderstandNoDetails ? '<p>✓ Understands no clinical details will be shared unless explicitly authorized</p>' : ''}
                ${data.eapUnderstandRefusalImpact ? '<p>✓ Understands refusal may impact ability to use EAP services</p>' : ''}
              </div>
            </div>
            ` : ''}
            
            <div class="review-section">
              <h3>Authorization Statement</h3>
              <p>I hereby authorize Avenues Unlimited to release or obtain the information specified above. I understand that this authorization is voluntary and that I may revoke this authorization at any time by providing written notice to Avenues Unlimited, except to the extent that action has already been taken in reliance on this authorization.</p>
            </div>
            
            <div class="review-section">
              <h3>Signatures</h3>
              ${data.clientSignatureData ? `
                <div>
                  <p><strong>Client Signature:</strong></p>
                  <img src="${data.clientSignatureData}" class="signature-img" alt="Client Signature">
                  <p><strong>Date:</strong> ${signedDate}</p>
                </div>
              ` : '<p><strong>Client Signature:</strong> Not provided</p>'}
              
              ${data.guardianSignatureData ? `
                <div style="margin-top: 15px;">
                  <p><strong>Guardian Signature:</strong></p>
                  <img src="${data.guardianSignatureData}" class="signature-img" alt="Guardian Signature">
                  <p><strong>Relationship:</strong> ${data.guardianRelationship}</p>
                  <p><strong>Date:</strong> ${signedDate}</p>
                </div>
              ` : ''}
              
              ${data.witnessSignatureData ? `
                <div style="margin-top: 15px;">
                  <p><strong>Witness:</strong> ${data.witnessName}</p>
                  <img src="${data.witnessSignatureData}" class="signature-img" alt="Witness Signature">
                  <p><strong>Date:</strong> ${signedDate}</p>
                </div>
              ` : ''}
            </div>
          </div>
          
          <div class="footer">
            <img src="https://www.avenuesportal.com/footer.png" alt="Avenues Footer">
          </div>
        </div>
        
        <script>
          window.onload = function() {
            window.focus();
            window.print();
          };
        </script>
      </body>
      </html>
    `;
  };

  // Signature handling functions
  const updateClientSignature = () => {
    if (clientSigRef.current && !clientSigRef.current.isEmpty()) {
      setClientSignatureData(clientSigRef.current.toDataURL());
    } else {
      setClientSignatureData(null);
    }
  };

  const updateGuardianSignature = () => {
    if (guardianSigRef.current && !guardianSigRef.current.isEmpty()) {
      setGuardianSignatureData(guardianSigRef.current.toDataURL());
    } else {
      setGuardianSignatureData(null);
    }
  };

  const updateWitnessSignature = () => {
    if (witnessSigRef.current && !witnessSigRef.current.isEmpty()) {
      setWitnessSignatureData(witnessSigRef.current.toDataURL());
    } else {
      setWitnessSignatureData(null);
    }
  };

  const clearClientSig = () => {
    if (clientSigRef.current) {
      clientSigRef.current.clear();
      setClientSignatureData(null);
    }
  };

  const clearGuardianSig = () => {
    if (guardianSigRef.current) {
      guardianSigRef.current.clear();
      setGuardianSignatureData(null);
    }
  };

  const clearWitnessSig = () => {
    if (witnessSigRef.current) {
      witnessSigRef.current.clear();
      setWitnessSignatureData(null);
    }
  };

  // Navigation handlers
  const nextStep = () => {
    if (currentStep === 1) {
      if (!formData.clientName.trim()) {
        alert('Please enter the client name');
        return;
      }
      if (!formData.authorizeReleaseTo.trim()) {
        alert('Please specify who to release information to');
        return;
      }
      
      // Check if at least one authorization type is selected (now combined)
      const hasAuthSelected = formData.shareProgressWithProvider || 
                             formData.coordinateCareWithPsychiatrist ||
                             formData.shareWithSchool ||
                             formData.speakWithFamily ||
                             formData.billUsingOtherCard ||
                             formData.provideDocumentationToProvider ||
                             formData.allowEmergencyContactInform ||
                             formData.schedulingInfoOnly ||
                             formData.treatmentProgress ||
                             formData.diagnosisAndPlan ||
                             formData.billingAndPayment ||
                             formData.fullClinicalRecords ||
                             formData.releaseOtherSpecify.trim();
      
      if (!hasAuthSelected) {
        alert('Please select at least one type of information to release');
        return;
      }
      
      // Check if at least one purpose is selected
      const hasPurposeSelected = formData.purposeCoordinationCare ||
                                formData.purposeBilling ||
                                formData.purposeEmergency ||
                                formData.purposeSupportFromFamily ||
                                formData.purposeOtherSpecify.trim();
      
      if (!hasPurposeSelected) {
        alert('Please select at least one purpose for this disclosure');
        return;
      }
      
      // Check if a duration is selected
      const hasDurationSelected = formData.durationOneYear ||
                                 formData.durationEndOfTreatment ||
                                 formData.durationSpecificDate;
      
      if (!hasDurationSelected) {
        alert('Please select how long this release will remain in effect');
        return;
      }
      
      if (!clientSignatureData) {
        alert('Client signature is required');
        return;
      }
      
      if (clientData.is_minor && !guardianSignatureData) {
        alert('Guardian signature is required for minors');
        return;
      }
      if (!signatureDate) {
        alert('Please enter the date signed');
        return;
      }
      if (signatureDate > todayEastern()) {
        alert('The date signed cannot be in the future');
        return;
      }
    }
    
    setCurrentStep(Math.min(currentStep + 1, totalSteps));
  };

  const prevStep = () => {
    setCurrentStep(Math.max(currentStep - 1, 1));
  };

  // Submit form
  const handleSubmit = () => {
    const signedOn = signatureDate || todayEastern();

    const roiData = {
      ...formData,
      clientSignatureData,
      clientSignatureDate: signedOn,
      guardianSignatureData: clientData.is_minor ? guardianSignatureData : null,
      guardianSignatureDate: clientData.is_minor && guardianSignatureData ? signedOn : null,
      witnessSignatureData,
      witnessSignatureDate: witnessSignatureData ? signedOn : null
    };

    fetch('/api/forms/roi/submit', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        token,
        roiData
      })
    })
    .then(res => {
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      return res.json();
    })
    .then(res => {
      if (res && res.success) {
        setSubmittedData(roiData);
        setSubmitted(true);
      } else {
        const errorMessage = (res && res.error) ? res.error : 'Unknown submission error';
        setErrorMsg(errorMessage);
        console.error('Submission returned error:', errorMessage);
      }
    })
    .catch(error => {
      console.error('Error submitting ROI form:', error);
      setErrorMsg(`Server error submitting ROI form: ${error.message}`);
    });
  };

  // Format date for display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    
    let dateObj;
    if (dateStr.includes('T')) {
      dateObj = new Date(dateStr);
    } else if (dateStr.includes('-')) {
      const [year, month, day] = dateStr.split('-');
      dateObj = new Date(year, month - 1, day);
    } else {
      return dateStr;
    }
    
    if (isNaN(dateObj)) return dateStr;
    
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const day = dateObj.getDate().toString().padStart(2, '0');
    const year = dateObj.getFullYear();
    
    return `${month}/${day}/${year}`;
  };

  // Loading and error states
  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <p>Loading...</p>
      </div>
    );
  }
  
  if (errorMsg) {
    return (
      <div style={styles.errorContainer}>
        <p style={styles.errorMessage}>{errorMsg}</p>
        <button 
          onClick={() => window.location.reload()} 
          style={styles.retryButton}
        >
          Try Again
        </button>
      </div>
    );
  }
  
  if (!isValid) {
    return (
      <div style={styles.errorContainer}>
        <p style={styles.errorMessage}>Invalid or expired link</p>
      </div>
    );
  }

  // Success state
  if (submitted) {
    return (
      <div style={styles.successContainer}>
        <h2>Thank you!</h2>
        <p>Your Release of Information form has been submitted successfully.</p>
        <p>The completed form has been sent to your therapist.</p>
        <button 
          onClick={handlePrint}
          style={styles.printButton}
        >
          Print Copy
        </button>
      </div>
    );
  }

  // Step 1: Main Form Page
  if (currentStep === 1) {
    return (
      <div style={styles.roiContainer}>
        <div style={styles.header}>
          <img 
            src="https://www.avenuesportal.com/letterhead.png" 
            alt="Avenues Unlimited" 
            style={styles.letterheadImg}
          />
        </div>
        
        <h2 style={styles.formTitle}>AUTHORIZATION FOR RELEASE OF INFORMATION</h2>
        
        {!allowEdits && (
          <div style={styles.readonlyNotice}>
            <span style={styles.readonlyIcon}>🔒</span>
            This form has been pre-filled by your therapist. Please review the information and proceed to sign.
          </div>
        )}
        
        {/* Client Information */}
        <div style={styles.formSection}>
          <h3 style={styles.sectionTitle}>Client Information</h3>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Client Name:</label>
            <input
              type="text"
              value={formData.clientName}
              onChange={(e) => handleInputChange('clientName', e.target.value)}
              style={{...styles.formInput, ...(allowEdits ? {} : styles.readonly)}}
              readOnly={!allowEdits}
              required
            />
          </div>
          
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Date of Birth:</label>
              <input
                type="date"
                value={formData.clientDob}
                onChange={(e) => handleInputChange('clientDob', e.target.value)}
                style={{...styles.formInput, ...(allowEdits ? {} : styles.readonly)}}
                readOnly={!allowEdits}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Phone:</label>
              <input
                type="tel"
                value={formData.clientPhone}
                onChange={(e) => handleInputChange('clientPhone', e.target.value)}
                style={{...styles.formInput, ...(allowEdits ? {} : styles.readonly)}}
                readOnly={!allowEdits}
              />
            </div>
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Address:</label>
            <textarea
              value={formData.clientAddress}
              onChange={(e) => handleInputChange('clientAddress', e.target.value)}
              style={{...styles.formTextarea, ...(allowEdits ? {} : styles.readonly)}}
              readOnly={!allowEdits}
              rows={2}
            />
          </div>
        </div>

        {/* Release Information To */}
        <div style={styles.formSection}>
          <h3 style={styles.sectionTitle}>This information may be released to or requested from:</h3>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Name:</label>
            <input
              type="text"
              value={formData.authorizeReleaseTo}
              onChange={(e) => handleInputChange('authorizeReleaseTo', e.target.value)}
              style={{...styles.formInput, ...(allowEdits ? {} : styles.readonly)}}
              readOnly={!allowEdits}
              required
            />
          </div>
          
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Relationship to client:</label>
              <input
                type="text"
                value={formData.releaseToRelationship}
                onChange={(e) => handleInputChange('releaseToRelationship', e.target.value)}
                style={{...styles.formInput, ...(allowEdits ? {} : styles.readonly)}}
                readOnly={!allowEdits}
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Phone/E-mail:</label>
              <input
                type="text"
                value={formData.releaseToPhone}
                onChange={(e) => handleInputChange('releaseToPhone', e.target.value)}
                style={{...styles.formInput, ...(allowEdits ? {} : styles.readonly)}}
                readOnly={!allowEdits}
              />
            </div>
          </div>
        </div>

        {/* COMBINED Authorization Section */}
        <div style={styles.formSection}>
          <h3 style={styles.sectionTitle}>I authorize Avenues Unlimited to release or obtain the following information:</h3>
          
          <div style={styles.checkboxList}>
            {[
              // Action-based authorizations
              { key: 'shareProgressWithProvider', text: 'Share progress and treatment details with my primary care provider or psychiatrist' },
              { key: 'coordinateCareWithPsychiatrist', text: 'Coordinate care with my psychiatrist or prescribing provider' },
              { key: 'shareWithSchool', text: 'Share treatment information with my child\'s school' },
              { key: 'speakWithFamily', text: 'Speak with a family member, partner, or support person' },
              { key: 'billUsingOtherCard', text: 'Bill services using a card not in my name' },
              { key: 'provideDocumentationToProvider', text: 'Provide documentation to another mental health provider' },
              { key: 'allowEmergencyContactInform', text: 'Allow emergency contacts to be informed in case of a crisis' },
              // Information type authorizations
              { key: 'schedulingInfoOnly', text: 'Scheduling or appointment information only' },
              { key: 'treatmentProgress', text: 'Treatment progress and participation' },
              { key: 'diagnosisAndPlan', text: 'Diagnosis and treatment plan' },
              { key: 'billingAndPayment', text: 'Billing and payment details' },
              { key: 'fullClinicalRecords', text: 'Full clinical records' }
            ].map((item) => (
              <label 
                key={item.key}
                style={{...styles.checkboxItem, ...(allowEdits ? {} : styles.checkboxReadonly)}}
              >
                <input
                  type="checkbox"
                  checked={formData[item.key]}
                  onChange={(e) => handleInputChange(item.key, e.target.checked)}
                  disabled={!allowEdits}
                  style={styles.checkbox}
                />
                <span style={styles.checkboxText}>{item.text}</span>
              </label>
            ))}
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Other:</label>
            <input
              type="text"
              value={formData.releaseOtherSpecify}
              onChange={(e) => handleInputChange('releaseOtherSpecify', e.target.value)}
              style={{...styles.formInput, ...(allowEdits ? {} : styles.readonly)}}
              readOnly={!allowEdits}
            />
          </div>
        </div>

        {/* Purpose of Disclosure */}
        <div style={styles.formSection}>
          <h3 style={styles.sectionTitle}>The purpose of disclosure:</h3>
          
          <div style={styles.checkboxList}>
            {[
              { key: 'purposeCoordinationCare', text: 'Coordination of care' },
              { key: 'purposeBilling', text: 'Billing' },
              { key: 'purposeEmergency', text: 'Emergency or safety planning' },
              { key: 'purposeSupportFromFamily', text: 'Support from family/friends' }
            ].map((item) => (
              <label 
                key={item.key}
                style={{...styles.checkboxItem, ...(allowEdits ? {} : styles.checkboxReadonly)}}
              >
                <input
                  type="checkbox"
                  checked={formData[item.key]}
                  onChange={(e) => handleInputChange(item.key, e.target.checked)}
                  disabled={!allowEdits}
                  style={styles.checkbox}
                />
                <span style={styles.checkboxText}>{item.text}</span>
              </label>
            ))}
          </div>
          
          <div style={styles.formGroup}>
            <label style={styles.label}>Other:</label>
            <input
              type="text"
              value={formData.purposeOtherSpecify}
              onChange={(e) => handleInputChange('purposeOtherSpecify', e.target.value)}
              style={{...styles.formInput, ...(allowEdits ? {} : styles.readonly)}}
              readOnly={!allowEdits}
            />
          </div>
        </div>

        {/* Duration */}
        <div style={styles.formSection}>
          <h3 style={styles.sectionTitle}>This release will remain in effect until (choose one):</h3>
          
          <div style={styles.checkboxList}>
            {[
              { key: 'durationOneYear', text: 'One year from today' },
              { key: 'durationEndOfTreatment', text: 'End of treatment' },
              { key: 'durationSpecificDate', text: 'Specific date:' }
            ].map((item) => (
              <label 
                key={item.key}
                style={{...styles.checkboxItem, ...(allowEdits ? {} : styles.checkboxReadonly)}}
              >
                <input
                  type="checkbox"
                  checked={formData[item.key]}
                  onChange={(e) => handleInputChange(item.key, e.target.checked)}
                  disabled={!allowEdits}
                  style={styles.checkbox}
                />
                <span style={styles.checkboxText}>{item.text}</span>
              </label>
            ))}
          </div>
          
          {formData.durationSpecificDate && (
            <div style={styles.formGroup}>
              <input
                type="date"
                value={formData.specificExpirationDate}
                onChange={(e) => handleInputChange('specificExpirationDate', e.target.value)}
                style={{...styles.formInput, ...(allowEdits ? {} : styles.readonly)}}
                readOnly={!allowEdits}
              />
            </div>
          )}
        </div>

        {/* EAP Section - Only show if isEAPClient is true */}
        {isEAPClient && (
          <div style={styles.formSection}>
            <h3 style={styles.sectionTitle}>If you are using Employee Assistance Program (EAP) services through your employer:</h3>
            
            <div style={styles.checkboxList}>
              {[
                { key: 'eapAuthorizeSharing', text: 'I authorize Avenues Unlimited to share session dates and information necessary for billing with my employer for the purpose of billing for services.' },
                { key: 'eapUnderstandNoDetails', text: 'I understand that no clinical details will be shared unless explicitly authorized.' },
                { key: 'eapUnderstandRefusalImpact', text: 'I understand that refusing this release may impact my ability to use EAP services at this practice.' }
              ].map((item) => (
                <label 
                  key={item.key}
                  style={{...styles.checkboxItem, ...(allowEdits ? {} : styles.checkboxReadonly)}}
                >
                  <input
                    type="checkbox"
                    checked={formData[item.key]}
                    onChange={(e) => handleInputChange(item.key, e.target.checked)}
                    disabled={!allowEdits}
                    style={styles.checkbox}
                  />
                  <span style={styles.checkboxText}>{item.text}</span>
                </label>
              ))}
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>My employer is:</label>
              <input
                type="text"
                value={formData.eapEmployer}
                onChange={(e) => handleInputChange('eapEmployer', e.target.value)}
                style={{...styles.formInput, ...(allowEdits ? {} : styles.readonly)}}
                readOnly={!allowEdits}
              />
            </div>
          </div>
        )}

        {/* Authorization Statement and Signatures */}
        <div style={styles.formSection}>
          <h3 style={styles.sectionTitle}>Authorization Statement</h3>
          <div style={styles.authorizationStatement}>
            <p>
              I hereby authorize Avenues Unlimited to release or obtain the information specified above. 
              I understand that this authorization is voluntary and that I may revoke this authorization 
              at any time by providing written notice to Avenues Unlimited, except to the extent that 
              action has already been taken in reliance on this authorization.
            </p>
          </div>
          
          <h3 style={styles.sectionTitle}>Signatures</h3>
          <div style={styles.formGroup}>
            <label style={styles.label}>Date Signed:</label>
            <input
              type="date"
              value={signatureDate}
              max={todayEastern()}
              onChange={(e) => setSignatureDate(e.target.value)}
              style={styles.formInput}
            />
            <div style={styles.signatureInstructions}>
              Defaults to today. If you signed this authorization on an earlier date, enter that date.
            </div>
          </div>

          <div style={styles.signatureSection}>
            <label style={styles.signatureLabel}>Client Signature *</label>
            <div style={styles.signatureContainer}>
              <SignatureCanvas 
                ref={clientSigRef} 
                penColor="black" 
                canvasProps={{ 
                  width: 400, 
                  height: 120, 
                  style: styles.signatureCanvas
                }} 
                onEnd={updateClientSignature}
              />
              <div style={styles.signatureInstructions}>Please sign above</div>
            </div>
            <button 
              type="button"
              onClick={clearClientSig} 
              style={styles.clearButton}
            >
              Clear Signature
            </button>
          </div>
          
          {clientData.is_minor && (
            <div style={styles.signatureSection}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Guardian Relationship:</label>
                <input
                  type="text"
                  value={formData.guardianRelationship}
                  onChange={(e) => handleInputChange('guardianRelationship', e.target.value)}
                  style={{...styles.formInput, ...(allowEdits ? {} : styles.readonly)}}
                  readOnly={!allowEdits}
                  placeholder="e.g., Parent, Legal Guardian"
                />
              </div>
              
              <label style={styles.signatureLabel}>Guardian Signature *</label>
              <div style={styles.signatureContainer}>
                <SignatureCanvas 
                  ref={guardianSigRef} 
                  penColor="black" 
                  canvasProps={{ 
                    width: 400, 
                    height: 120, 
                    style: styles.signatureCanvas
                  }} 
                  onEnd={updateGuardianSignature}
                />
                <div style={styles.signatureInstructions}>Guardian, please sign above</div>
              </div>
              <button 
                type="button"
                onClick={clearGuardianSig} 
                style={styles.clearButton}
              >
                Clear Guardian Signature
              </button>
            </div>
          )}
          
          <div style={styles.signatureSection}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Witness Name (optional):</label>
              <input
                type="text"
                value={formData.witnessName}
                onChange={(e) => handleInputChange('witnessName', e.target.value)}
                style={{...styles.formInput, ...(allowEdits ? {} : styles.readonly)}}
                readOnly={!allowEdits}
              />
            </div>
            
            {formData.witnessName && (
              <>
                <label style={styles.signatureLabel}>Witness Signature</label>
                <div style={styles.signatureContainer}>
                  <SignatureCanvas 
                    ref={witnessSigRef} 
                    penColor="black" 
                    canvasProps={{ 
                      width: 400, 
                      height: 120, 
                      style: styles.signatureCanvas
                    }} 
                    onEnd={updateWitnessSignature}
                  />
                  <div style={styles.signatureInstructions}>Witness, please sign above</div>
                </div>
                <button 
                  type="button"
                  onClick={clearWitnessSig} 
                  style={styles.clearButton}
                >
                  Clear Witness Signature
                </button>
              </>
            )}
          </div>
        </div>
        
        <div style={styles.navigationButtons}>
          <button onClick={nextStep} style={styles.nextButton}>
            Review & Submit
          </button>
        </div>
      </div>
    );
  }

  // Step 2: Review Page
  return (
    <div style={styles.roiContainer}>
      <div style={styles.header}>
        <img 
          src="https://www.avenuesportal.com/letterhead.png" 
          alt="Avenues Unlimited" 
          style={styles.letterheadImg}
        />
      </div>
      
      <h2 style={styles.formTitle}>AUTHORIZATION FOR RELEASE OF INFORMATION - REVIEW</h2>
      
      <div style={styles.reviewDocument}>
        <div style={styles.reviewSection}>
          <h3 style={styles.reviewSectionTitle}>Client Information</h3>
          <p><strong>Name:</strong> {formData.clientName}</p>
          <p><strong>Date of Birth:</strong> {formatDate(formData.clientDob)}</p>
          <p><strong>Phone:</strong> {formData.clientPhone}</p>
          <p><strong>Address:</strong> {formData.clientAddress}</p>
        </div>
        
        <div style={styles.reviewSection}>
          <h3 style={styles.reviewSectionTitle}>Information Release Authorization</h3>
          <p><strong>Release information to:</strong> {formData.authorizeReleaseTo}</p>
          <p><strong>Relationship:</strong> {formData.releaseToRelationship}</p>
          <p><strong>Contact:</strong> {formData.releaseToPhone}</p>
        </div>
        
        <div style={styles.reviewSection}>
          <h3 style={styles.reviewSectionTitle}>Authorized Information Release</h3>
          <div style={styles.reviewCheckboxes}>
            {formData.shareProgressWithProvider && <p>✓ Share progress and treatment details with my primary care provider or psychiatrist</p>}
            {formData.coordinateCareWithPsychiatrist && <p>✓ Coordinate care with my psychiatrist or prescribing provider</p>}
            {formData.shareWithSchool && <p>✓ Share treatment information with my child's school</p>}
            {formData.speakWithFamily && <p>✓ Speak with a family member, partner, or support person</p>}
            {formData.billUsingOtherCard && <p>✓ Bill services using a card not in my name</p>}
            {formData.provideDocumentationToProvider && <p>✓ Provide documentation to another mental health provider</p>}
            {formData.allowEmergencyContactInform && <p>✓ Allow emergency contacts to be informed in case of a crisis</p>}
            {formData.schedulingInfoOnly && <p>✓ Scheduling or appointment information only</p>}
            {formData.treatmentProgress && <p>✓ Treatment progress and participation</p>}
            {formData.diagnosisAndPlan && <p>✓ Diagnosis and treatment plan</p>}
            {formData.billingAndPayment && <p>✓ Billing and payment details</p>}
            {formData.fullClinicalRecords && <p>✓ Full clinical records</p>}
            {formData.releaseOtherSpecify && <p>✓ Other: {formData.releaseOtherSpecify}</p>}
          </div>
        </div>
        
        <div style={styles.reviewSection}>
          <h3 style={styles.reviewSectionTitle}>Purpose of Disclosure</h3>
          <div style={styles.reviewCheckboxes}>
            {formData.purposeCoordinationCare && <p>✓ Coordination of care</p>}
            {formData.purposeBilling && <p>✓ Billing</p>}
            {formData.purposeEmergency && <p>✓ Emergency or safety planning</p>}
            {formData.purposeSupportFromFamily && <p>✓ Support from family/friends</p>}
            {formData.purposeOtherSpecify && <p>✓ Other: {formData.purposeOtherSpecify}</p>}
          </div>
        </div>
        
        <div style={styles.reviewSection}>
          <h3 style={styles.reviewSectionTitle}>Duration</h3>
          <p>
            {formData.durationOneYear && 'One year from today'}
            {formData.durationEndOfTreatment && 'End of treatment'}
            {formData.durationSpecificDate && `Until: ${formatDate(formData.specificExpirationDate)}`}
          </p>
        </div>
        
        {/* Only show EAP section in review if it's an EAP client */}
        {isEAPClient && (formData.eapAuthorizeSharing || formData.eapEmployer) && (
          <div style={styles.reviewSection}>
            <h3 style={styles.reviewSectionTitle}>Employee Assistance Program (EAP)</h3>
            {formData.eapEmployer && <p><strong>Employer:</strong> {formData.eapEmployer}</p>}
            {formData.eapAuthorizeSharing && <p>✓ Authorized to share session dates and billing information with employer</p>}
            {formData.eapUnderstandNoDetails && <p>✓ Understands no clinical details will be shared unless explicitly authorized</p>}
            {formData.eapUnderstandRefusalImpact && <p>✓ Understands refusal may impact ability to use EAP services</p>}
          </div>
        )}
        
        <div style={styles.reviewSection}>
          <h3 style={styles.reviewSectionTitle}>Authorization Statement</h3>
          <p style={styles.authorizationText}>
            I hereby authorize Avenues Unlimited to release or obtain the information specified above. 
            I understand that this authorization is voluntary and that I may revoke this authorization 
            at any time by providing written notice to Avenues Unlimited, except to the extent that 
            action has already been taken in reliance on this authorization.
          </p>
        </div>
        
        <div style={styles.reviewSection}>
          <h3 style={styles.reviewSectionTitle}>Signatures</h3>
          <p><strong>Client Signature:</strong> {clientSignatureData ? 'Signed' : 'Not signed'}</p>
          {clientData.is_minor && (
            <p><strong>Guardian Signature:</strong> {guardianSignatureData ? 'Signed' : 'Not signed'}</p>
          )}
          {formData.witnessName && (
            <p><strong>Witness Signature:</strong> {witnessSignatureData ? 'Signed' : 'Not signed'}</p>
          )}
          <p><strong>Date:</strong> {formatDate(signatureDate)}</p>
        </div>
      </div>
      
      <div style={styles.footer}>
        <img 
          src="https://www.avenuesportal.com/footer.png" 
          alt="Avenues Footer" 
          style={styles.footerImg}
        />
      </div>
      
      <div style={styles.navigationButtons}>
        <button onClick={prevStep} style={styles.backButton}>
          Back to Form
        </button>
        <button onClick={handleSubmit} style={styles.submitButton}>
          Submit Authorization
        </button>
      </div>
    </div>
  );
}

// Styles object (same as before)
const styles = {
  roiContainer: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: "'Times New Roman', Times, serif",
    lineHeight: '1.5',
    color: 'var(--text-strong)',
    background: '#fff'
  },
  header: {
    textAlign: 'center',
    marginBottom: '30px'
  },
  letterheadImg: {
    maxWidth: '100%',
    height: 'auto'
  },
  footer: {
    textAlign: 'center',
    marginTop: '40px',
    marginBottom: '20px'
  },
  footerImg: {
    maxWidth: '100%',
    height: 'auto'
  },
  formTitle: {
    textAlign: 'center',
    color: 'var(--primary-color)',
    fontSize: '20px',
    fontWeight: 'bold',
    marginBottom: '30px',
    padding: '15px',
    border: '2px solid var(--primary-color)',
    background: 'var(--surface-muted)'
  },
  readonlyNotice: {
    background: 'linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%)',
    border: '2px solid #ffd700',
    borderRadius: '10px',
    padding: '15px 20px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    color: '#856404',
    fontWeight: '600',
    boxShadow: '0 2px 10px rgba(255, 215, 0, 0.2)'
  },
  readonlyIcon: {
    fontSize: '24px'
  },
  formSection: {
    marginBottom: '30px',
    border: '1px solid var(--border-color)',
    padding: '20px',
    borderRadius: '5px',
    background: 'var(--surface-muted)'
  },
  sectionTitle: {
    marginTop: '0',
    marginBottom: '15px',
    color: 'var(--primary-color)',
    fontSize: '16px',
    fontWeight: 'bold',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '8px'
  },
  formGroup: {
    marginBottom: '15px'
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '15px'
  },
  label: {
    display: 'block',
    marginBottom: '5px',
    fontWeight: 'bold',
    color: 'var(--text-strong)',
    fontSize: '14px'
  },
  formInput: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'inherit',
    transition: 'border-color 0.3s ease',
    boxSizing: 'border-box'
  },
  formTextarea: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    fontSize: '14px',
    fontFamily: 'inherit',
    transition: 'border-color 0.3s ease',
    resize: 'vertical',
    boxSizing: 'border-box'
  },
  readonly: {
    background: 'var(--surface-muted)',
    borderColor: 'var(--border-color)',
    color: 'var(--text-muted)'
  },
  checkboxList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    marginBottom: '20px'
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'flex-start',
    cursor: 'pointer',
    padding: '15px',
    border: '1px solid var(--border-color)',
    borderRadius: '6px',
    background: '#fff',
    borderLeft: '4px solid var(--border-color)',
    transition: 'all 0.2s ease',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  checkboxReadonly: {
    background: 'var(--surface-muted)',
    borderColor: 'var(--border-color)',
    cursor: 'not-allowed',
    opacity: '0.7'
  },
  checkbox: {
    marginRight: '12px',
    marginTop: '2px',
    transform: 'scale(1.3)',
    flexShrink: '0',
    accentColor: '#2a5885'
  },
  checkboxText: {
    lineHeight: '1.5',
    fontSize: '14px',
    color: 'var(--text-strong)',
    fontWeight: '500'
  },
  authorizationStatement: {
    backgroundColor: '#f0f8ff',
    border: '1px solid #b0d4f1',
    borderRadius: '6px',
    padding: '15px',
    margin: '15px 0',
    fontSize: '14px',
    lineHeight: '1.6',
    color: 'var(--primary-color)'
  },
  authorizationText: {
    fontSize: '14px',
    lineHeight: '1.6',
    color: 'var(--primary-color)',
    margin: '0',
    fontStyle: 'italic'
  },
  signatureSection: {
    marginBottom: '30px',
    padding: '20px',
    border: '2px solid #2a5885',
    borderRadius: '8px',
    background: 'linear-gradient(135deg, #f8f9ff 0%, #f0f8ff 100%)'
  },
  signatureLabel: {
    display: 'block',
    fontWeight: 'bold',
    color: 'var(--primary-color)',
    marginBottom: '15px',
    fontSize: '16px'
  },
  signatureContainer: {
    border: '3px solid #2a5885',
    borderRadius: '8px',
    margin: '15px 0',
    display: 'inline-block',
    background: 'white',
    padding: '10px',
    boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.1), 0 4px 8px rgba(42, 88, 133, 0.1)',
    position: 'relative'
  },
  signatureCanvas: {
    borderRadius: '4px',
    display: 'block'
  },
  signatureInstructions: {
    textAlign: 'center',
    color: 'var(--text-muted)',
    fontStyle: 'italic',
    fontSize: '12px',
    marginTop: '5px',
    padding: '5px',
    background: 'var(--surface-muted)',
    borderRadius: '4px'
  },
  clearButton: {
    background: 'var(--error-color)',
    color: 'white',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500',
    marginTop: '10px',
    transition: 'background-color 0.2s ease'
  },
  navigationButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '30px',
    paddingTop: '20px',
    borderTop: '2px solid var(--border-color)',
    gap: '15px'
  },
  backButton: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    transition: 'background-color 0.3s ease',
    background: 'var(--text-muted)',
    color: 'white'
  },
  nextButton: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    transition: 'background-color 0.3s ease',
    background: '#2a5885',
    color: 'white'
  },
  submitButton: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    transition: 'background-color 0.3s ease',
    background: 'var(--success-color)',
    color: 'white'
  },
  printButton: {
    padding: '12px 24px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: 'bold',
    transition: 'background-color 0.3s ease',
    background: '#17a2b8',
    color: 'white'
  },
  loadingContainer: {
    textAlign: 'center',
    padding: '40px 20px',
    maxWidth: '600px',
    margin: '0 auto'
  },
  errorContainer: {
    textAlign: 'center',
    padding: '40px 20px',
    maxWidth: '600px',
    margin: '0 auto'
  },
  successContainer: {
    textAlign: 'center',
    padding: '40px 20px',
    maxWidth: '600px',
    margin: '0 auto'
  },
  errorMessage: {
    color: 'var(--error-color)',
    fontSize: '16px',
    marginBottom: '20px'
  },
  retryButton: {
    background: '#2a5885',
    color: 'white',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px'
  },
  reviewDocument: {
    border: '2px solid var(--primary-color)',
    padding: '30px',
    margin: '20px 0',
    background: '#fff'
  },
  reviewSection: {
    marginBottom: '25px',
    paddingBottom: '15px',
    borderBottom: '1px solid var(--border-color)'
  },
  reviewSectionTitle: {
    color: 'var(--primary-color)',
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '10px',
    borderBottom: '1px solid var(--border-color)',
    paddingBottom: '5px'
  },
  reviewCheckboxes: {
    paddingLeft: '10px'
  }
};